import {
  DEFAULT_ACCENT,
  empty,
  loading,
  type Locale,
  type ScreenState,
  type ThemeMode,
} from "@planner/core";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { loadConfig, type RuntimeConfig } from "./config.js";
import { useLocale } from "./i18n/provider.js";
import { platform } from "./platform/index.js";
import { Screen } from "./shell/Screen.js";
import { StepUpHost } from "./shell/StepUp.js";
import { RequestAccessHost } from "./shell/RequestAccess.js";
import { Shell } from "./shell/Shell.js";
import { useActionCount } from "./shell/actionCount.js";
import { WhatsNew } from "./shell/WhatsNew.js";
import { PushAsk } from "./shell/PushAsk.js";
import { useToast } from "./shell/Toast.js";
import { destinationOf, type Destination } from "./shell/destination.js";
import { createClient, signOut, useSession, type Session } from "./session.js";
import { RuntimeConfigProvider } from "./config-context.js";
import { RealtimeProvider } from "./realtime/context.js";
import { NAV_ITEMS, PORTAL_NAV_ITEMS } from "./shell/nav.js";
import { LANDED_PORTAL_SCREENS } from "@planner/core";
import { applyTheme, preferredMode, rememberMode } from "./theme/apply.js";

/**
 * جذر التطبيق.
 *
 * يقرأ الإعداد، يفتح الجلسة، ويسلّم القوقعة. ولا يعرف شيئاً عن أي شاشة —
 * الشاشات تنزل مع موديولاتها، وكلٌّ منها يُغلَّف بـ`Screen` فيرث الحالات الأربع
 * بلا أن يكتبها.
 */

/**
 * **ما اختاره صاحبُ الحساب يُطبَّق — ولو دخل من جهازٍ لم يره قبل.**
 *
 * فاللغةُ والمظهر كانا في تخزين المتصفّح وحده: **يحفظهما على اللابتوب ويفتح
 * التليفون فيجد النظام بلغةٍ لم يخترها**، فيُعيد الاختيار على كل جهاز —
 * **والاختيارُ الذي يُعاد ليس اختياراً، هو إزعاج**.
 *
 * ويجيئان في نفس نداء الإقلاع (`/me`) الذي تنتظره القوقعةُ قبل أن ترسم، فلا
 * رحلةَ زائدة ولا انقلابٌ أمام العين.
 *
 * **وخطّافٌ لا مكوّن** — عن قصد: فالشجرةُ تتفرّق إلى ثلاث عودات (إلزامُ كلمة
 * السرّ، وقوقعةُ البورتال، وقوقعةُ الموظّف)، **ومكوّنٌ كان سيُركَّب في ثلاثة
 * مواضع يُنسى في واحد** — وهو بالضبط الموضعُ الذي سيُستعمَل.
 *
 * **و«زيّ الجهاز» تعني الجهازَ فعلاً**: تتبع نظامَ صاحبها وتتغيّر معه، وإلّا
 * صارت اسماً لقيمةٍ جامدة.
 */
function useAccountPreferences(
  state: ScreenState<Session>,
  onMode: (mode: ThemeMode) => void,
): void {
  const { locale: shown, setLocale } = useLocale();
  const wanted = state.status === "ready" ? state.data.locale : null;
  const theme = state.status === "ready" ? state.data.theme : null;

  /**
   * **ولغةُ الحساب تُطبَّق حين تصل أو تتغيّر — لا كلّما اختلفت.**
   *
   * **وكانت تُطبَّق على كلّ اختلاف**: فمن ضغط زرَّ اللغة يغيّرها، **فتجد هذه
   * الحلقةُ خلافاً بين ما يراه وما على حسابه فتُعيدها في اللحظة نفسِها** —
   * فيبدو الزرُّ ميتاً وهو يعمل. وقياسُ «اختلفا» يجعل الحلقةَ تخاصم
   * المستخدمَ؛ **وقياسُ «وصلت قيمةٌ جديدة» يجعلها تخدمه**.
   */
  const applied = useRef<Locale | null>(null);
  useEffect(() => {
    if (wanted === null || wanted === applied.current) return;
    applied.current = wanted;
    if (wanted !== shown) setLocale(wanted);
  }, [wanted, setLocale, shown]);

  useEffect(() => {
    if (theme === null) return;
    onMode(theme === "system" ? platform.preferredColorScheme() : theme);
  }, [theme, onMode]);
}

/**
 * **كلُّ شاشةٍ تُحمَّل حين تُفتَح** — لا كلُّها مع أوّل زيارة.
 *
 * فكان ملفٌّ واحدٌ بـ١٫١ ميجا يُنزَّل على الهاتف قبل أن تظهر الرئيسية، **وفيه ثلاثون
 * شاشةً لا يفتح منها الموظّفُ إلا واحدة**، والعميلُ لا يفتح أيّاً من شاشات الموظفين.
 * وملفُّ شاشةٍ من نسخةٍ سابقة لا يُوجد بعد الرفع يُقال بكوده (`APP_UPDATED`).
 */
const Home = lazy(() => import("./screens/Home.js").then((m) => ({ default: m.Home })));
const Settings = lazy(() => import("./screens/Settings.js").then((m) => ({ default: m.Settings })));
const Moderation = lazy(() =>
  import("./screens/Moderation.js").then((m) => ({ default: m.Moderation })),
);
const Reports = lazy(() => import("./screens/Reports.js").then((m) => ({ default: m.Reports })));
const Analytics = lazy(() =>
  import("./screens/Analytics.js").then((m) => ({ default: m.Analytics })),
);
const Profile = lazy(() => import("./screens/Profile.js").then((m) => ({ default: m.Profile })));
const SignInOrSetup = lazy(() =>
  import("./screens/SetupWizard.js").then((m) => ({ default: m.SignInOrSetup })),
);
const Invite = lazy(() => import("./screens/Invite.js").then((m) => ({ default: m.Invite })));
const ErrorCatalogue = lazy(() =>
  import("./screens/ErrorCatalogue.js").then((m) => ({ default: m.ErrorCatalogue })),
);
const People = lazy(() => import("./screens/People.js").then((m) => ({ default: m.People })));
const Roles = lazy(() => import("./screens/Roles.js").then((m) => ({ default: m.Roles })));
const Departments = lazy(() =>
  import("./screens/Departments.js").then((m) => ({ default: m.Departments })),
);
const AccessRequests = lazy(() =>
  import("./screens/AccessRequests.js").then((m) => ({ default: m.AccessRequests })),
);
const Tasks = lazy(() => import("./screens/Tasks.js").then((m) => ({ default: m.Tasks })));
const Notifications = lazy(() =>
  import("./screens/Notifications.js").then((m) => ({ default: m.Notifications })),
);
const Clients = lazy(() => import("./screens/Clients.js").then((m) => ({ default: m.Clients })));
const Storage = lazy(() => import("./screens/Storage.js").then((m) => ({ default: m.Storage })));
const Actions = lazy(() => import("./screens/Actions.js").then((m) => ({ default: m.Actions })));
const Audit = lazy(() => import("./screens/Audit.js").then((m) => ({ default: m.Audit })));
const Health = lazy(() => import("./screens/Health.js").then((m) => ({ default: m.Health })));
const Changelog = lazy(() =>
  import("./screens/Changelog.js").then((m) => ({ default: m.Changelog })),
);
const NotificationAdmin = lazy(() =>
  import("./screens/NotificationAdmin.js").then((m) => ({ default: m.NotificationAdmin })),
);
const Plans = lazy(() => import("./screens/Plans.js").then((m) => ({ default: m.Plans })));
const MyWork = lazy(() => import("./screens/MyWork.js").then((m) => ({ default: m.MyWork })));
const ChangePassword = lazy(() =>
  import("./screens/ChangePassword.js").then((m) => ({ default: m.ChangePassword })),
);
const PortalBody = lazy(() =>
  import("./portal/Portal.js").then((m) => ({ default: m.PortalBody })),
);
const Chat = lazy(() => import("./screens/Chat.js").then((m) => ({ default: m.Chat })));
const Meetings = lazy(() => import("./screens/Meetings.js").then((m) => ({ default: m.Meetings })));
const Board = lazy(() => import("./screens/Board.js").then((m) => ({ default: m.Board })));
const Boards = lazy(() => import("./screens/Board.js").then((m) => ({ default: m.Boards })));

export function App(): ReactNode {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [mode, setMode] = useState<ThemeMode>(preferredMode);
  /** **يُقرأ مرّةً عند الإقلاع ثم يُمحى من العنوان** */
  const [invite, setInvite] = useState<string | null>(() => {
    const token = platform.queryValue("invite");
    /**
     * **وتنظيفُ شريط العنوان لا يُبلَّغ به المستخدم.**
     *
     * فنجاحُه لا يعنيه، وفشلُه لا حيلةَ له فيه — غايتُه ألّا يبقى الرمزُ في
     * التاريخ ولا يُنسَخ مع الرابط. وتجاهلُه **مكتوبٌ هنا** لا مبتلَعٌ في
     * `catch` فارغ.
     */
    if (token !== null) void platform.dropQueryValue("invite");
    return token;
  });
  /**
   * **وأدعوةٌ هي أم استعادة؟** — يُقرأ مع الرمز ويُمحى معه.
   *
   * ولا يحكم شيئاً: الرمزُ وحده يفتح الحساب. **وغايتُه سطرُ شرحٍ صادق** —
   * «ده أول دخول ليك» لمن يُستعاد له كذبةٌ صغيرة لا داعيَ لها.
   */
  const [resetFlow] = useState<boolean>(() => {
    const flag = platform.queryValue("reset") === "1";
    if (flag) void platform.dropQueryValue("reset");
    return flag;
  });

  useEffect(() => {
    void loadConfig().then(setConfig);
  }, []);

  useEffect(() => platform.onColorSchemeChange(setMode), []);

  /** **وEscape يقفل النافذةَ اللي فوق** — للموظف وللعميل، من موضعٍ واحد في المنصّة */
  useEffect(() => platform.onEscapeCloseDialog(), []);

  /**
   * **الثيم تُطبّق هنا، لا في القوقعة.**
   *
   * كانت القوقعة هي من يضع التوكنز، ففتحتُ المتصفّح فوجدتُ شاشة الخطأ
   * **بلا أي تنسيق**: الحالات الأربع تُرسَم قبل القوقعة أحياناً، فكل `var(--x)` بلا
   * قيمة. وهذا بالضبط «الصفحة البيضاء» التي نبني ضدّها — وفي أسوأ لحظة:
   * حين يكون شيءٌ ما معطوباً أصلاً.
   *
   * والجذر يسبق كل شيء، فهنا موضعها.
   */
  useEffect(() => {
    applyTheme(config?.accentColor ?? DEFAULT_ACCENT, mode);
  }, [config, mode]);

  /**
   * **واسمُ التبويب اسمُ النظام** — من نفس المصدر الذي يأتي منه اللون.
   *
   * **وكان `Planner` مكتوباً في `index.html`**: فمشترٍ سمّى نظامَه باسمه يرى
   * اسمَنا في تبويبه، وفي إشاراته المرجعية، **وفي اسم التطبيق بعد تثبيته على
   * هاتفه**. وهو عطبُ «إعدادٌ يُخبز وقت البناء» في آخر موضعٍ بقي فيه.
   */
  useEffect(() => {
    if (config) platform.setDocumentTitle(config.systemName);
  }, [config]);

  /** **وتطبيقُ أندرويد يُجهِّز قناةَ تنبيهه باسم النظام** — وفي المتصفّح لا يفعل شيئاً */
  useEffect(() => {
    if (config) platform.prepareNativePush(config.systemName);
  }, [config]);

  if (!config) return <Booting />;

  /**
   * **ورابطُ الدعوة يُقرأ قبل الجلسة** — فلا جلسةَ لمن لم يدخل بعد.
   *
   * ويُمسَح الرمزُ من شريط العنوان فورَ قراءته: يبقى وإلا في التاريخ، ويُنسَخ
   * مع الرابط، ويظهر في أي لقطة شاشة. **ويُقرَّر هنا لا في أثر**: التحويلُ في
   * `useEffect` يرسم الشاشةَ الخطأ إطاراً على الأقل.
   */
  if (invite !== null) {
    return (
      <Invite
        client={createClient(config)}
        token={invite}
        reset={resetFlow}
        onDone={() => {
          setInvite(null);
        }}
      />
    );
  }

  /**
   * **ومضيفُ الإعداد فوق كل شاشة** — فلا تُمرَّر عناوينُ السيرفر بروباً.
   *
   * وتفرّعت الشجرةُ تحته إلى خمس عودات (دعوةٌ، وإلزامُ كلمة سرّ، وبورتال،
   * وقوقعةُ موظّف، وشاشةُ خطأ)، **فلو رُكّب في كلٍّ منها لنُسي في واحدة** —
   * وهي التي يسقط فيها كلُّ وجهٍ على الشاشة.
   */
  return (
    <RuntimeConfigProvider config={config}>
      <Session config={config} mode={mode} onModeChange={setMode} />
    </RuntimeConfigProvider>
  );
}

/** لحظةٌ واحدة قبل قراءة الإعداد — هيكل، لا شاشة بيضاء */
function Booting(): ReactNode {
  return (
    <div className="state" role="status" aria-busy="true">
      <div className="sk-wrap" aria-hidden="true">
        <div className="sk" style={{ width: "40%" }} />
        <div className="sk" />
      </div>
    </div>
  );
}

function Session({
  config,
  mode,
  onModeChange,
}: {
  readonly config: RuntimeConfig;
  readonly mode: ThemeMode;
  readonly onModeChange: (mode: ThemeMode) => void;
}): ReactNode {
  const { t, setLocale } = useLocale();
  const toast = useToast();
  const [current, setCurrent] = useState("home");
  const [openTaskId, setOpenTaskId] = useState<string | undefined>(undefined);
  const [unread, setUnread] = useState(0);
  /**
   * البورد المفتوح، وعميلُه إن جاء من ملفّه.
   *
   * **ولا يُحفَظان في العنوان بعد** — التوجيه بالعنوان ينزل مع البورتال في
   * المرحلة السادسة حيث يُحتاج للروابط العميقة الحقيقية. وحتى ذلك الحين
   * الانتقال داخليّ، ومعلَنٌ أنه كذلك لا مسكوتٌ عنه.
   */
  const [boardsOfClient, setBoardsOfClient] = useState<string | null>(null);
  const [openBoardId, setOpenBoardId] = useState<string | null>(null);
  /**
   * **الصفُّ الذي جاء الرابط العميق إليه** — تُبرِزه الشاشة وتفتحه.
   *
   * ويُمسَح عند الانتقال من القائمة: من دخل الشاشة بنفسه لا يُلاحقه صفُّ
   * إشعارٍ قديم.
   */
  const [focusId, setFocusId] = useState<string | null>(null);
  /** **ونوعُ الصفّ مع معرّفه** — فشاشةٌ تسكنها كيانان تعرف أيَّهما فُتح */
  const [focusEntity, setFocusEntity] = useState<string | null>(null);
  /** **ونوعُ «اللي عليك» الذي فتحته بطاقةُ الرئيسية** — فتُعرَض صفوفُه وحدها */
  const [actionsKind, setActionsKind] = useState<string | null>(null);
  /** كارتٌ جاء رابطٌ عميق إليه — يُفتَح في بورده */
  const [openCardId, setOpenCardId] = useState<string | null>(null);

  /**
   * **لا يُعاد تحميل الصفحة عند انتهاء الجلسة.**
   *
   * كتبتُ ذلك أول مرة، ففتحتُ المتصفّح فوجدتُ **مئات الطلبات المرفوضة**:
   * الطلب يُرفض ـ الصفحة تُعاد ـ الطلب يُرفض… دورةٌ لا تقف، وشاشة بيضاء
   * لا ترسم شيئاً، وسيرفر يُقصَف. وإعادة التحميل ردّاً على طلب فاشل **خطأ دائماً**:
   * تُعيد تشغيل الطلب نفسه الذي فشل توّاً.
   *
   * الصحيح: تظهر حالة الخطأ بكودها ورقم ربطها وزرّ إعادة يضغطه المستخدم.
   * وحين تنزل شاشة الدخول تأخذ `onSessionInvalid` لتنتقل إليها مرة واحدة.
   */
  const client = useMemo(() => createClient(config), [config]);

  const session = useSession(client);
  useAccountPreferences(session.state, onModeChange);
  /**
   * **وعدّادُ «اللي عليك» على الشريط** — من نفس قائمة الشاشة، ويُعاد سؤالُه مع
   * كلّ انتقال. **ومن لا يحمل المركزَ لا يُسأل له** — فلا رفضٌ يُطلَب بلا سبب.
   */
  const actionCount = useActionCount(
    client,
    current,
    session.state.status === "ready" &&
      session.state.data.capabilities.held.has("actions.view_own"),
  );

  /**
   * **وتثبيتُ المظهر في موضعٍ واحد** — في الجهاز وفي الحساب معاً.
   *
   * فالجهازُ يحتاجه **قبل أول رسم** (وإلّا وميضٌ أبيض قبل أن يصل `/me`)،
   * والحسابُ يحتاجه ليمشي مع صاحبه إلى أي جهازٍ يدخل منه. ولو كُتب في الجهاز
   * وحده — كما كان — **لما عرفه السيرفرُ أبداً**، ولانقلب على نفسه حين يُحدَّث
   * نداءُ الجلسة.
   *
   * **وفشلُه يُقال**: لا يُبتلَع في `catch` فارغ. فمن ضغط الزرَّ ولم يُحفَظ
   * اختيارُه يعرف لماذا، بكودٍ يقوله كما هو.
   */
  const changeMode = useCallback(
    (next: ThemeMode) => {
      onModeChange(next);
      rememberMode(next);
      void client.request("/me", { method: "PATCH", body: { theme: next } }).then((result) => {
        if (!result.ok) toast.failed(result.error);
      });
    },
    [client, onModeChange, toast],
  );

  /**
   * **واللغةُ مثلُه بالضبط — وكان زرُّها لا يفعل شيئاً.**
   *
   * **فقد كان يكتب في الجهاز وحده**، ولغةُ الحساب تأتي مع `/me` فتجد خلافاً
   * فتُعيدها: يضغط المستخدمُ الزرَّ، **فتتغيّر اللغةُ ثم ترتدّ في اللحظة
   * نفسِها** — فيبدو الزرُّ ميتاً وهو يعمل. وهو المنزلقُ الذي وُثِّق في
   * القوقعة للمظهر **ثم وقعت فيه اللغة**: لأن العلاجَ كُتب لحالةٍ لا لصنف.
   *
   * **فصارت من نفس الباب**: الجهازُ ليقرأها قبل أوّل رسم، **والحسابُ لتمشي
   * مع صاحبها** إلى أيّ جهاز. وفشلُ الحفظ يُقال بكوده، ولا يُبتلَع.
   */
  const changeLocale = useCallback(
    (next: Locale) => {
      setLocale(next);
      void client.request("/me", { method: "PATCH", body: { locale: next } }).then((result) => {
        if (!result.ok) toast.failed(result.error);
      });
    },
    [client, setLocale, toast],
  );

  /**
   * الإقلاع نفسه بالحالات الأربع: فشلُ `/me` يعرض كوده ورقم ربطه وزرّ إعادة.
   * وقبل الجلسة لا قوقعة، لأن القائمة تُبنى من الصلاحيات ولا نعرفها بعد.
   */
  /**
   * **جلسة مرفوضة ليست خطأً — بل دعوة للدخول.**
   *
   * وعرضها كشاشة خطأ بكودها يجعل أوّل ما يراه موظّف في صباحه «تعذّر تحميل
   * الشاشة» — وهذا ليس صحيحاً ولا مفيداً. أمّا باقي أسباب الفشل فتبقى على
   * شكلها: كود ورقم ربط وزرّ إعادة.
   */
  if (session.state.status === "error" && session.state.error.code === "SESSION_INVALID") {
    return (
      <SignInOrSetup
        client={client}
        onSignedIn={session.reload}
        systemName={config.systemName}
        mailEnabled={config.mailEnabled}
      />
    );
  }

  if (session.state.status !== "ready") {
    return (
      <Screen state={session.state} onRetry={session.reload}>
        {() => null}
      </Screen>
    );
  }

  const me = session.state.data;

  /**
   * **ومن أُلزم بتغيير كلمته لا يرى شيئاً قبلها** — ولا يُحبَس.
   *
   * فكلُّ حسابٍ يُنشئه مديرٌ يُولَد بالإلزام، **والحارسُ يرفض كلَّ مسارٍ عداه**.
   * وكانت هذه الشاشةُ غائبةً تماماً: يدخل الموظّفُ فيُقال له «غيّر كلمتك» ولا
   * يجد ما يغيّر به، **ولا يستطيع حتى الخروج**. وظهر يوم نزل البورتال، لأن
   * **جهةَ الاتصال تُولَد في هذه الحالة بعينها**.
   *
   * ويُقرَّر **قبل القوقعة**: فالقائمةُ تُبنى من قدراتٍ لا تعمل أيٌّ منها الآن.
   */
  if (me.mustChangePassword) {
    return (
      <ChangePassword
        client={client}
        forced
        onDone={session.reload}
        onSignOut={() => {
          void signOut(client).then(session.reload);
        }}
      />
    );
  }

  /**
   * **والقوقعةُ تُختار بنوع الحساب لا بالعنوان** (§4.5) — **وقبل الرسم**.
   *
   * فجلسةٌ بورتالية ترى قوقعةَ البورتال وحدها. وفي v2 كان التحويلُ في
   * `useEffect`، **فيُرسَم جسمُ الشاشة الخطأ إطاراً على الأقل** — وميضُ شاشةٍ
   * ليست له، في كل فتحة.
   */
  if (me.accountKind === "portal") {
    return (
      <PortalShell
        config={config}
        client={client}
        me={me}
        mode={mode}
        onModeChange={changeMode}
        onLocaleChange={changeLocale}
        onSignOut={() => {
          void signOut(client).then(session.reload);
        }}
      />
    );
  }

  /**
   * **عنوانُ الشاشة اسمُها في القائمة** — لا جدولٌ ثانٍ بجانبها.
   *
   * وكان جدولاً منسوخاً نسي سبعَ شاشات (الخطط والشات والتقارير والطابور…)،
   * **فقال الشريطُ «الرئيسية» فوق كلٍّ منها**.
   */
  const titleKey = NAV_ITEMS.find((item) => item.key === current)?.labelKey ?? "nav.home";

  /**
   * **تنفيذُ وجهةٍ محسوبة** — والترجمةُ في موضعها الواحد قبلها.
   *
   * والكارتُ وحدَه يحتاج سؤالاً: رابطُه يحمل معرّفه لا معرّف بورده، والبوردُ
   * هو الشاشة. فيُسأل عنه مرّةً ثم تُفتَح لوحتُه — **ولو لم يُعرَف بورده قيل
   * ذلك بكوده**، لا شاشةٌ فارغة ولا «غير موجود» على بوردٍ لا وجود له.
   *
   * وكتبتُها أوّلاً تفتح معرّفَ الكارت **كأنه بورد**، فأعطت «غير موجود» بكودها
   * عند أوّل نقرة. والخطأُ الصريح هو ما جعلها تُكتشَف في دقيقة.
   */
  async function goTo(destination: Destination): Promise<void> {
    setFocusId(destination.focusId);
    setFocusEntity(destination.entity);
    setOpenCardId(null);

    if (destination.entity === "task") {
      setOpenTaskId(destination.focusId ?? undefined);
      setCurrent("tasks");
      return;
    }

    if (destination.entity === "plan_item") {
      setCurrent("plans");
      return;
    }

    if (destination.entity === "board") {
      setBoardsOfClient(null);
      setOpenBoardId(destination.focusId);
      setCurrent("boards");
      return;
    }

    if (destination.entity === "card" && destination.focusId !== null) {
      const result = await client.request<{ readonly board: { readonly id: string } }>(
        `/cards/${destination.focusId}`,
      );
      if (!result.ok) {
        toast.failed(result.error);
        return;
      }
      setBoardsOfClient(null);
      setOpenBoardId(result.data.board.id);
      setOpenCardId(destination.focusId);
      setCurrent("boards");
      return;
    }

    setCurrent(destination.screen);
  }

  return (
    /**
     * **والقناةُ اللحظية على القوقعة الداخلية وحدها.**
     *
     * **ولا تُركَّب في البورتال أبداً** (§7): فالحضورُ ممنوعٌ عند العميل —
     * «ظهورُها تسريبُ هوية» — **والسيرفرُ يرفض جلستَه في المصافحة** على كل حال.
     */
    <RealtimeProvider enabled>
      <Shell
        systemName={config.systemName}
        minwalMark={config.minwalMark}
        accent={config.accentColor}
        capabilities={me.capabilities}
        user={{ id: me.id, name: me.name, photoUpdatedAt: me.photoUpdatedAt }}
        current={current}
        onNavigate={(key) => {
          // فتحُ المهام من القائمة يبدأ من القائمة، لا من مهمة إشعارٍ قديم
          if (key === "tasks") setOpenTaskId(undefined);
          /** والانتقال من القائمة إلى البوردات يبدأ من كلّها، لا من عميلٍ سابق */
          if (key === "boards") {
            setBoardsOfClient(null);
            setOpenBoardId(null);
          }
          setFocusId(null);
          setFocusEntity(null);
          setActionsKind(null);
          setCurrent(key);
        }}
        badges={{ notifications: unread, actions: actionCount.count }}
        mode={mode}
        onModeChange={changeMode}
        onLocaleChange={changeLocale}
        onOpenProfile={() => {
          setFocusId(null);
          setFocusEntity(null);
          setActionsKind(null);
          setCurrent("profile");
        }}
        onSignOut={() => {
          void signOut(client).then(session.reload);
        }}
        title={t(titleKey)}
      >
        {/*
         * لا شاشة نزلت بعد، فالقوقعة تقول ذلك **بالحالة الفارغة**: ماذا يُفترض
         * أن يكون هنا ولماذا ليس بعد. لا صفحةٌ بيضاء ولا «قريباً».
         */}
        <Suspense fallback={<ScreenLoading />}>
          {current === "my-work" ? (
            <MyWork
              client={client}
              timeZone={config.systemTimezone}
              onNavigate={(destination) => {
                void goTo(destination);
              }}
            />
          ) : current === "clients" ? (
            <Clients
              client={client}
              focusId={focusId}
              timeZone={config.systemTimezone}
              canCreate={me.capabilities.held.has("clients.manage")}
              onOpenBoards={(clientId) => {
                setBoardsOfClient(clientId);
                setOpenBoardId(null);
                setCurrent("boards");
              }}
              onOpenPlan={
                me.capabilities.held.has("plans.view")
                  ? (planId) => {
                      const destination = destinationOf(`/plans/${planId}`);
                      if (destination) void goTo(destination);
                    }
                  : null
              }
            />
          ) : current === "boards" ? (
            openBoardId ? (
              <Board
                client={client}
                boardId={openBoardId}
                timeZone={config.systemTimezone}
                openCardId={openCardId}
                meId={me.id}
                onBack={() => {
                  setOpenBoardId(null);
                  setOpenCardId(null);
                }}
              />
            ) : (
              <Boards
                client={client}
                clientId={boardsOfClient}
                onOpen={setOpenBoardId}
                onBack={() => {
                  setBoardsOfClient(null);
                  setCurrent("clients");
                }}
                canManageTemplates={me.capabilities.held.has("boards.templates.manage")}
              />
            )
          ) : current === "tasks" ? (
            <Tasks
              client={client}
              timeZone={config.systemTimezone}
              canCreate={me.capabilities.held.has("tasks.create")}
              openTaskId={openTaskId}
            />
          ) : current === "notifications" ? (
            <Notifications
              client={client}
              timeZone={config.systemTimezone}
              onUnreadChange={setUnread}
              /**
               * **وتُنفَّذ الوجهة هنا** — والترجمةُ في موضعها الواحد.
               *
               * فالشاشة لا تعرف شيئاً عن حالة القوقعة، والقوقعة لا تقرأ نصّ رابط.
               */
              onNavigate={(destination) => {
                void goTo(destination);
              }}
            />
          ) : current === "meetings" ? (
            <Meetings client={client} timeZone={config.systemTimezone} focusId={focusId} />
          ) : current === "chat" ? (
            <Chat client={client} meId={me.id} timeZone={config.systemTimezone} focusId={focusId} />
          ) : current === "plans" ? (
            <Plans
              client={client}
              timeZone={config.systemTimezone}
              focusId={focusId}
              focusEntity={focusEntity}
              canManage={me.capabilities.held.has("plans.manage")}
              canCreateItems={me.capabilities.held.has("plans.items.create")}
              canManageTypes={me.capabilities.held.has("plans.item_types.manage")}
              canToggleVisibility={me.capabilities.held.has("plans.items.visibility")}
            />
          ) : current === "storage" ? (
            <Storage client={client} timeZone={config.systemTimezone} focusId={focusId} />
          ) : current === "people" ? (
            <People
              client={client}
              focusId={focusId}
              canManage={me.capabilities.held.has("identity.users.manage")}
              timeZone={config.systemTimezone}
            />
          ) : current === "roles" ? (
            <Roles
              client={client}
              focusId={focusId}
              canManage={me.capabilities.held.has("roles.manage")}
            />
          ) : current === "departments" ? (
            <Departments
              client={client}
              focusId={focusId}
              timeZone={config.systemTimezone}
              canDelegate={me.capabilities.held.has("org.delegation.manage")}
              canManage={me.capabilities.held.has("org.departments.manage")}
            />
          ) : current === "access" ? (
            <AccessRequests
              client={client}
              focusId={focusId}
              canResolve={me.capabilities.held.has("access_requests.resolve")}
            />
          ) : current === "home" ? (
            <Home
              client={client}
              userName={me.name}
              timeZone={config.systemTimezone}
              onOpen={(destination, kind) => {
                setActionsKind(kind ?? null);
                setCurrent(destination);
              }}
              onNavigate={(destination) => {
                void goTo(destination);
              }}
              {...(me.capabilities.held.has("platform.audit.view")
                ? {
                    onOpenAllActivity: () => {
                      setCurrent("audit");
                    },
                  }
                : {})}
            />
          ) : current === "analytics" ? (
            <Analytics
              client={client}
              timeZone={config.systemTimezone}
              focusId={focusId}
              canCompany={me.capabilities.held.has("analytics.company.view")}
              canDepartment={me.capabilities.held.has("analytics.department.view")}
              canRanking={me.capabilities.held.has("analytics.ranking.view")}
              canClients={me.capabilities.held.has("analytics.clients.view")}
              canGoals={me.capabilities.held.has("analytics.goals.manage")}
              canExport={me.capabilities.held.has("analytics.export")}
            />
          ) : current === "reports" ? (
            <Reports
              client={client}
              meId={me.id}
              focusId={focusId}
              canCreate={me.capabilities.held.has("reports.create")}
              canReviewHead={me.capabilities.held.has("reports.review_head")}
              canReviewAdmin={me.capabilities.held.has("reports.review_admin")}
              canAskException={me.capabilities.held.has("reports.exception.request")}
            />
          ) : current === "moderation" ? (
            <Moderation
              client={client}
              timeZone={config.systemTimezone}
              focusId={focusId}
              canPublish={me.capabilities.held.has("moderation.publish")}
              canWithdraw={me.capabilities.held.has("moderation.unpublish")}
              canSchedule={me.capabilities.held.has("moderation.schedule")}
              canResolve={me.capabilities.held.has("moderation.takedown.resolve")}
            />
          ) : current === "actions" ? (
            <Actions
              client={client}
              timeZone={config.systemTimezone}
              onNavigate={(destination) => {
                void goTo(destination);
              }}
              onlyKind={actionsKind}
              onShowAll={() => {
                setActionsKind(null);
              }}
              onCountChange={actionCount.report}
            />
          ) : current === "audit" ? (
            <Audit
              client={client}
              timeZone={config.systemTimezone}
              focusId={focusId}
              canPurge={me.capabilities.held.has("platform.audit.purge")}
              onNavigate={(destination) => {
                void goTo(destination);
              }}
            />
          ) : current === "health" ? (
            <Health
              client={client}
              timeZone={config.systemTimezone}
              canResolve={me.capabilities.held.has("platform.incidents.resolve")}
            />
          ) : current === "changelog" ? (
            <Changelog
              client={client}
              timeZone={config.systemTimezone}
              meId={me.id}
              focusId={focusId}
              canManage={me.capabilities.held.has("platform.changelog.manage")}
            />
          ) : current === "notifications-admin" ? (
            <NotificationAdmin client={client} />
          ) : current === "settings" ? (
            <Settings client={client} timeZone={config.systemTimezone} />
          ) : current === "profile" ? (
            <Profile
              client={client}
              profile={me}
              onSaved={session.reload}
              timeZone={config.systemTimezone}
              pushPublicKey={config.pushPublicKey}
              canPush={me.capabilities.held.has("identity.push.manage_own")}
            />
          ) : current === "errors" ? (
            <ErrorCatalogue client={client} />
          ) : (
            <Screen
              state={empty<never>()}
              onRetry={session.reload}
              empty={{ titleKey: "shell.noScreenYet", bodyKey: "shell.noScreenYetBody" }}
            >
              {() => null}
            </Screen>
          )}
        </Suspense>
        {/**
         * **ونافذةُ التحقّق الثاني فوق كل شاشة** — تُركَّب مرّةً هنا حيث يوجد
         * عميلُ الشبكة، فتخدم العملياتِ الثمانيَ الكبيرة كلَّها.
         */}
        <StepUpHost client={client} />
        {/**
         * **ومضيفُ طلب الصلاحية فوق كل شاشة** — فشاشةُ المنع تطلب من موضعها،
         * ولا تحتاج شاشةٌ أن تتذكّر تمريرَ شيء.
         */}
        <RequestAccessHost client={client} />
        {/**
         * **ونافذةُ «إيه الجديد» فوق كل شاشة** — تُركَّب مرّةً هنا، **ولكل من
         * دخل**: فالخبرُ عن النظام الذي يستعملونه، لا عن شاشةٍ بعينها.
         */}
        <WhatsNew client={client} />
        {/**
         * **وسؤالُ التنبيه مرّةً على كل جهاز** — لمن يحمل قدرتَه وحده، فلا يُسأل
         * من سيُرفَض اشتراكُه.
         */}
        {me.capabilities.held.has("identity.push.manage_own") ? (
          <PushAsk client={client} pushPublicKey={config.pushPublicKey} />
        ) : null}
      </Shell>
    </RealtimeProvider>
  );
}

/**
 * قوقعةُ البورتال — **نفسُ القوقعة، بقائمةِ بنودٍ أخرى**.
 *
 * ولا نسخةَ ثانية من الإطار: نفسُ الشريط السفلي، ونفسُ «المزيد»، ونفسُ تخطّي
 * المحتوى، ونفسُ الوضع الليلي. **ونسخةٌ ثانية** كانت ستنحرف في أوّل تعديل —
 * فيُضاف زرٌّ للموظّفين ولا يُضاف للعميل، أو يُصلَح عطبٌ في واحدةٍ وحدها.
 *
 * **والبنودُ من سجلّ شاشات البورتال**: ما نزل يظهر، وما أُجِّل لا بندَ له —
 * وبوابةٌ تكسر البناء يومَ ينزل موديولُه وشاشتُه غائبة.
 */
function PortalShell({
  config,
  client,
  me,
  mode,
  onModeChange,
  onLocaleChange,
  onSignOut,
}: {
  readonly config: RuntimeConfig;
  readonly client: ReturnType<typeof createClient>;
  readonly me: Session;
  readonly mode: ThemeMode;
  readonly onModeChange: (mode: ThemeMode) => void;
  readonly onLocaleChange: (next: Locale) => void;
  readonly onSignOut: () => void;
}): ReactNode {
  const { t } = useLocale();
  const toast = useToast();
  const [current, setCurrent] = useState("portal-home");
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [unread, setUnread] = useState(0);

  const titleKey =
    LANDED_PORTAL_SCREENS.find((screen) => screen.key === current)?.labelKey ?? "portal.nav.home";

  return (
    <Shell
      systemName={config.systemName}
      minwalMark={config.minwalMark}
      accent={config.accentColor}
      capabilities={me.capabilities}
      items={PORTAL_NAV_ITEMS}
      user={{ id: me.id, name: me.name, photoUpdatedAt: me.photoUpdatedAt }}
      current={current}
      onNavigate={(key) => {
        /** والعودةُ من القائمة تبدأ من القائمة، لا من عنصرٍ مفتوحٍ سابقاً */
        setOpenItemId(null);
        setCurrent(key);
      }}
      badges={{ "portal-notifications": unread }}
      alerts={{
        count: unread,
        label: t("portal.alerts", { n: unread }),
        onOpen: () => {
          setOpenItemId(null);
          setCurrent("portal-notifications");
        },
      }}
      mode={mode}
      onModeChange={onModeChange}
      onLocaleChange={onLocaleChange}
      onSignOut={onSignOut}
      title={t(titleKey)}
    >
      <Suspense fallback={<ScreenLoading />}>
        <PortalBody
          client={client}
          current={current}
          pushPublicKey={config.pushPublicKey}
          openItemId={openItemId}
          onOpenItem={setOpenItemId}
          meName={me.name}
          canMeet={me.capabilities.held.has("portal.meetings.request")}
          onGo={(screen) => {
            setOpenItemId(null);
            setCurrent(screen);
          }}
          canAskReportEdit={me.capabilities.held.has("portal.reports.request_edit")}
          notifications={
            <Notifications
              client={client}
              timeZone={config.systemTimezone}
              onUnreadChange={setUnread}
              /**
               * **ورابطُ العميل بورتاليّ** — فيفتح لوحتَه هو.
               *
               * ووجهةُ الرابط تُعلن لأي قوقعةٍ هي، فوجهةٌ داخلية في جلسةٍ بورتالية
               * **لا تُنفَّذ**: تُقال بكودها بدل أن تفتح شاشةً تُرفَض.
               */
              onNavigate={(destination) => {
                if (destination.shell !== "portal") {
                  toast.failed({
                    code: "PORTAL_OPERATION_REFUSED",
                    messageKey: "errors.portalOperationRefused",
                    correlationId: "",
                  });
                  return;
                }
                /**
                 * **وبشاشتها لا بافتراضٍ أنها عنصر.**
                 *
                 * وكان كلُّ وجهةٍ بورتالية تُفتَح **لوحةَ عنصر**: فيوم نزل ردُّ
                 * الوكالة على الشات، كان رابطُه يصل بمعرّف **رسالة** فتُفتَح
                 * لوحةُ عنصرٍ لا وجود له — «العنصر مش موجود» في وجه من جاء يقرأ
                 * ردّاً وصله. والوجهةُ تُعلن شاشتَها، **فتُقرأ منها**.
                 */
                if (destination.screen === "portal-item") {
                  setOpenItemId(destination.focusId);
                  return;
                }
                setOpenItemId(null);
                setCurrent(destination.screen);
              }}
            />
          }
          /**
           * **و«اللي عليك» شاشةُ الموظّف نفسُها** (قرار شادي الثاني).
           *
           * **ولا نسخةٌ ثانية**: البنودُ محسوبةٌ في السيرفر من صلاحياته هو،
           * **ونسخةٌ ثانيةٌ كانت ستنحرف في أوّل تعديل** — فيُضاف مصدرٌ للموظفين
           * ولا يُضاف للعميل.
           */
          actions={
            <Actions
              client={client}
              timeZone={config.systemTimezone}
              onNavigate={(destination) => {
                if (destination.shell !== "portal") {
                  toast.failed({
                    code: "PORTAL_OPERATION_REFUSED",
                    messageKey: "errors.portalOperationRefused",
                    correlationId: "",
                  });
                  return;
                }
                if (destination.screen === "portal-item") {
                  setOpenItemId(destination.focusId);
                  return;
                }
                setOpenItemId(null);
                setCurrent(destination.screen);
              }}
            />
          }
        />
      </Suspense>
    </Shell>
  );
}

/** **وانتظارُ ملفّ الشاشة حالةُ تحميلٍ بهيكلها** — لا صفحةٌ بيضاء */
export function ScreenLoading(): ReactNode {
  return (
    <Screen state={loading()} onRetry={() => undefined}>
      {() => null}
    </Screen>
  );
}
